import { useMemo, useState } from 'react';
import type { FormEvent } from 'react';
import { Banner, Button, Caption, Link, Section, SegmentedControl } from '@telegram-apps/telegram-ui';
import { ApiError } from '../lib/api';
import { useCreateProductShoot } from '../hooks/useGenerationMutations';
import type { ProductShootParams } from '../types/generation';

interface Props {
  productId: string;
  intent?: ProductShootParams['intent'];
  onDone: (generationId: string) => void;
  onCancel: () => void;
}

type ErrCode = 'limit_exceeded' | 'product_not_ready' | 'product_has_no_image' | string;

function parseApiCode(error: unknown): ErrCode | null {
  if (!(error instanceof ApiError)) return null;
  try {
    const parsed = JSON.parse(error.body) as { error?: string };
    return parsed.error ?? null;
  } catch {
    return null;
  }
}

export function ProductShootForm({ productId, intent = 'studio_model', onDone, onCancel }: Props) {
  const createShoot = useCreateProductShoot();
  const [modelType, setModelType] = useState<ProductShootParams['modelType']>('female');
  const [size, setSize] = useState<ProductShootParams['size']>('m');
  const [background, setBackground] = useState<ProductShootParams['background']>(
    intent === 'lifestyle_shoot' ? 'lifestyle' : 'white',
  );
  const [numSamples, setNumSamples] = useState<ProductShootParams['numSamples']>(1);

  const scaleEnabled = __SCALE_PLAN_ENABLED__ === true;
  const adminBot = import.meta.env.VITE_ADMIN_BOT_USERNAME ?? '';
  const errorCode = parseApiCode(createShoot.error);

  const errorMessage = useMemo(() => {
    if (!createShoot.error) return '';
    if (errorCode === 'product_not_ready') return 'Фото товара ещё обрабатывается, попробуй через 30 секунд';
    if (errorCode === 'product_has_no_image') return 'У товара нет фото, загрузите сначала фото';
    if (errorCode === 'limit_exceeded') return 'Лимит исчерпан в этом месяце. Увеличьте тариф в FitBot Business.';
    return (createShoot.error as Error)?.message ?? 'Не удалось запустить генерацию';
  }, [createShoot.error, errorCode]);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    try {
      const result = await createShoot.mutateAsync({
        productId,
        intent,
        modelType,
        size,
        background,
        numSamples,
      });
      onDone(result.id);
    } catch {
      /* haptic handled in useCreateProductShoot.onError */
    }
  };

  return (
    <form onSubmit={handleSubmit}>
      <Section header="Размер модели">
        <SegmentedControl>
          <SegmentedControl.Item type="button" selected={size === 's'} onClick={() => setSize('s')}>
            S
          </SegmentedControl.Item>
          <SegmentedControl.Item type="button" selected={size === 'm'} onClick={() => setSize('m')}>
            M
          </SegmentedControl.Item>
          <SegmentedControl.Item type="button" selected={size === 'xl'} onClick={() => setSize('xl')}>
            XL
          </SegmentedControl.Item>
        </SegmentedControl>
      </Section>

      <Section header="Тип модели">
        <SegmentedControl>
          <SegmentedControl.Item type="button" selected={modelType === 'female'} onClick={() => setModelType('female')}>
            Женская
          </SegmentedControl.Item>
          <SegmentedControl.Item type="button" selected={modelType === 'male'} onClick={() => setModelType('male')}>
            Мужская
          </SegmentedControl.Item>
          <SegmentedControl.Item type="button" selected={modelType === 'neutral'} onClick={() => setModelType('neutral')}>
            Нейтральная
          </SegmentedControl.Item>
        </SegmentedControl>
      </Section>

      <Section header="Стиль">
        <SegmentedControl>
          <SegmentedControl.Item type="button" selected={background === 'white'} onClick={() => setBackground('white')}>
            Каталог
          </SegmentedControl.Item>
          <SegmentedControl.Item type="button" selected={background === 'studio'} onClick={() => setBackground('studio')}>
            Студия
          </SegmentedControl.Item>
          <SegmentedControl.Item type="button" selected={background === 'lifestyle'} onClick={() => setBackground('lifestyle')}>
            Lifestyle
          </SegmentedControl.Item>
          <SegmentedControl.Item
            type="button"
            selected={background === 'marketplace'}
            onClick={() => setBackground('marketplace')}
          >
            Маркетплейс
          </SegmentedControl.Item>
        </SegmentedControl>
      </Section>

      <Section header="Варианты">
        <SegmentedControl>
          <SegmentedControl.Item type="button" selected={numSamples === 1} onClick={() => setNumSamples(1)}>
            1 вариант
          </SegmentedControl.Item>
          <SegmentedControl.Item
            type="button"
            selected={numSamples === 4}
            onClick={() => setNumSamples(4)}
            disabled={!scaleEnabled}
          >
            4 варианта
          </SegmentedControl.Item>
        </SegmentedControl>
        {!scaleEnabled && (
          <Caption level="2" style={{ marginTop: 8 }}>
            Доступно после volume deal
          </Caption>
        )}
      </Section>

      {createShoot.isError && (
        <Section header=" ">
          <Banner type="section">{errorMessage}</Banner>
          {errorCode === 'limit_exceeded' && adminBot && (
            <div style={{ marginTop: 8 }}>
              <Link href={`https://t.me/${adminBot}`} target="_blank" rel="noreferrer noopener">
                Открыть FitBot Business
              </Link>
            </div>
          )}
        </Section>
      )}

      <Section header=" ">
        <Button stretched mode="filled" type="submit" loading={createShoot.isPending} disabled={createShoot.isPending}>
          Сгенерировать фото
        </Button>
        <Button stretched mode="plain" type="button" onClick={onCancel} disabled={createShoot.isPending}>
          Отмена
        </Button>
      </Section>
    </form>
  );
}
